// The small icons of the file list, one for each kind of file.
//
// They are SVG files in icons/filetypes. A list can have thousands of rows,
// so a row does not carry an image of its own: it has a class, like
// "fi fi-pdf", and one style sheet gives each class its picture as a
// background. The pictures are drawn from the SVG once, into small bitmaps
// sharp enough for the screen, and every row with the same icon shows the
// same bitmap. Until the bitmaps are made, and if they cannot be, the SVG
// itself is the background.

import { fileIconKinds, type FileIconKind } from "./file-type";

// the SVG files as text, by path
// @ts-ignore import.meta.glob is vite's
const svgFiles: Record<string, string> = import.meta.glob("./icons/filetypes/*.svg", {
  query: "?raw",
  import: "default",
  eager: true,
});

// the size of an icon in CSS pixels
export const fileIconSize = 16;

const styleId = "fm-file-icons";

function svgOfKind(kind: FileIconKind): string {
  return svgFiles[`./icons/filetypes/${kind}.svg`] || "";
}

function svgDataURL(svg: string): string {
  return "data:image/svg+xml," + encodeURIComponent(svg.trim());
}

// Returns the style sheet for pictures given by kind.
export function fileIconsCSS(urls: Partial<Record<FileIconKind, string>>): string {
  let px = fileIconSize + "px";
  let css = `.fi{display:inline-block;width:${px};height:${px};background-size:${px} ${px};background-repeat:no-repeat;vertical-align:top}\n`;
  for (let kind of fileIconKinds) {
    let url = urls[kind];
    if (url) {
      css += `.fi-${kind}{background-image:url("${url}")}\n`;
    }
  }
  return css;
}

// Draws an SVG into a bitmap of size x size pixels and returns its URL.
async function rasterize(svg: string, size: number): Promise<string> {
  let img = new Image();
  img.decoding = "async";
  img.src = svgDataURL(svg);
  await img.decode();
  let canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  let ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("no 2d context");
  }
  ctx.drawImage(img, 0, 0, size, size);
  let blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) {
    throw new Error("cannot make a bitmap");
  }
  return URL.createObjectURL(blob);
}

let installed = false;

// Makes the icon classes work in the page. Does it once; safe to call from
// every file list.
export function installFileIcons() {
  if (installed) {
    return;
  }
  installed = true;
  let style = document.createElement("style");
  style.id = styleId;
  let svgURLs: Partial<Record<FileIconKind, string>> = {};
  for (let kind of fileIconKinds) {
    let svg = svgOfKind(kind);
    if (svg) {
      svgURLs[kind] = svgDataURL(svg);
    }
  }
  style.textContent = fileIconsCSS(svgURLs);
  document.head.appendChild(style);

  // Bitmaps of at least twice the size: sharp on dense screens and when the
  // page is zoomed in.
  let scale = Math.min(4, Math.max(2, Math.ceil(window.devicePixelRatio || 1)));
  let size = fileIconSize * scale;
  let kinds = fileIconKinds.filter((kind) => svgURLs[kind]);
  Promise.all(kinds.map((kind) => rasterize(svgOfKind(kind), size)))
    .then((bitmapURLs) => {
      let urls: Partial<Record<FileIconKind, string>> = {};
      kinds.forEach((kind, i) => (urls[kind] = bitmapURLs[i]));
      // all at once: the list is drawn again one time
      style.textContent = fileIconsCSS(urls);
      style.dataset.bitmaps = String(size);
    })
    .catch((e) => {
      // the SVG backgrounds stay
      console.log("installFileIcons: no bitmaps:", e);
    });
}
